// Add / Edit a component limitation — the slide-in over the Aircraft Item Details tab
// (component editor, 2026-10-07). Chrome is the shared Sheet (BP-89); the save is
// POST / PATCH /v6/items/{id}/limitations, the rules are app/items/limitations.py.
//
// Legacy (intent): the inline row editor of mod_parts/includes/tabs/parts_tab_item_aircraft.inc
// :598-660 -> save_limitation (parts_ajax_handler.inc:2090-2229). Its fields: Based On, Period,
// Type, Finite Life, Warning + unit, Date On / Due Off, @A/C Unit Fitted, @Install TSN (+ UNK)
// and TSO. @Install TSN is TSN AT INSTALL (total_life_used); the totals are computed, never
// typed, so they are not on this form.
import React, { useMemo, useRef } from "react";
import { Autocomplete, Checkbox, FormControlLabel, Grid, MenuItem, TextField as MuiTextField } from "@mui/material";
import { Field, Form, Formik, FormikHelpers, FormikProps } from "formik";
import { TextField } from "formik-mui";
import { useSnackbar } from "notistack";

import { axios } from "../../../api";
import { ActionButton, CancelButton } from "../../../components/Buttons";
import Sheet, { SheetActions, SheetContent, SheetHeader } from "../../../components/Sheet";
import TenantDatePicker from "../../../components/TenantDatePicker";
import { useConfig } from "../../../hooks/useConfig";
import { handleAPIErrors } from "../../../utils/FormikErrors";
import { BASED_ON_OPTIONS, WARNING_UNIT_OPTIONS } from "../../../utils/limitationVocab";
import {
  ComponentLimitationRow,
  componentTypeOptions,
  emptyLimitationForm,
  isConditionAxis,
  isDateAxis,
  limitationFormErrors,
  limitationFormValues,
  LimitationFormValues,
  limitationPayload,
} from "../componentLimitations";

interface Props {
  open: boolean;
  itemId: number;
  /** The row being edited; null adds a new limitation. */
  row: ComponentLimitationRow | null;
  /** Fitted to an aircraft: the install figures apply. */
  fitted: boolean;
  /** Based On values already on this item (the stored spellings, e.g. "Engine1 Hours"). */
  knownBasedOn: string[];
  onClose: () => void;
  onSaved: () => void;
}

const ComponentLimitationSheet: React.FC<Props> = ({ open, itemId, row, fitted, knownBasedOn, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef<FormikProps<LimitationFormValues>>(null);
  const aircraftConfig = useConfig("aircraft");
  const typeOptions = useMemo(
    // Served prefix-stripped (src/hooks/__tests__/configKeyShape.test.ts).
    () => componentTypeOptions(aircraftConfig?.component_type_list),
    [aircraftConfig]
  );
  const basedOnOptions = useMemo(
    () => Array.from(new Set([...knownBasedOn.filter(Boolean), ...BASED_ON_OPTIONS])),
    [knownBasedOn]
  );
  const initialValues = useMemo(() => (row ? limitationFormValues(row) : emptyLimitationForm()), [row]);

  const submit = (values: LimitationFormValues, helpers: FormikHelpers<LimitationFormValues>) => {
    const errors = limitationFormErrors(values, fitted);
    if (Object.keys(errors).length) {
      helpers.setErrors(errors);
      helpers.setSubmitting(false);
      return;
    }
    const body = limitationPayload(values, fitted);
    const request = row
      ? axios.patch(`/v6/items/${itemId}/limitations/${row.id}`, body, { cache: false })
      : axios.post(`/v6/items/${itemId}/limitations`, body, { cache: false });
    request
      .then(() => {
        helpers.setSubmitting(false);
        enqueueSnackbar(row ? "Limitation saved" : "Limitation added", { variant: "success" });
        onSaved();
      })
      .catch((error) => {
        // a failed write keeps the sheet open with what was typed (BP-89)
        helpers.setSubmitting(false);
        handleAPIErrors(error, helpers.setErrors);
        const detail = error?.response?.data?.detail;
        enqueueSnackbar(typeof detail === "string" ? detail : "Error saving limitation", { variant: "error" });
      });
  };

  return (
    <Sheet open={open} onClose={onClose}>
      <SheetHeader onClose={onClose} title={row ? `Edit Limitation ${row.id}` : "Add Limitation"} />
      <SheetContent>
        {open ? (
          <Formik initialValues={initialValues} onSubmit={submit} innerRef={formRef} enableReinitialize>
            {({ values, setFieldValue, errors }) => {
              const dateAxis = isDateAxis(values.based_on);
              const condition = isConditionAxis(values.based_on);
              return (
                <Form className="mui-form" data-testid="component-limitation-form">
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        freeSolo
                        options={basedOnOptions}
                        value={values.based_on}
                        onInputChange={(_e, v) => setFieldValue("based_on", v ?? "")}
                        renderInput={(params) => (
                          <MuiTextField
                            {...params}
                            label="Based On"
                            required
                            error={Boolean(errors.based_on)}
                            helperText={errors.based_on}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field component={TextField} select fullWidth name="limitation_type" label="Type">
                        <MenuItem value="">—</MenuItem>
                        {typeOptions.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Field>
                    </Grid>
                    {!condition ? (
                      <>
                        <Grid item xs={6}>
                          <Field component={TextField} fullWidth name="life" label="Period" type="number" />
                        </Grid>
                        <Grid item xs={6}>
                          <Field component={TextField} fullWidth name="life_hours" label="Finite Life" type="number" />
                        </Grid>
                        <Grid item xs={6}>
                          <Field component={TextField} fullWidth name="warning" label="Warning" type="number" />
                        </Grid>
                        <Grid item xs={6}>
                          <Field component={TextField} select fullWidth name="warning_unit" label="Warning Unit">
                            <MenuItem value="">—</MenuItem>
                            {WARNING_UNIT_OPTIONS.map((u) => (
                              <MenuItem key={u} value={u}>
                                {u}
                              </MenuItem>
                            ))}
                          </Field>
                        </Grid>
                      </>
                    ) : null}
                    {fitted ? (
                      <>
                        <Grid item xs={6}>
                          <TenantDatePicker
                            label="Date On"
                            required
                            fullWidth
                            value={values.install_date}
                            onChange={(iso) => setFieldValue("install_date", iso)}
                            error={Boolean(errors.install_date)}
                            helperText={errors.install_date}
                          />
                        </Grid>
                        {!dateAxis ? (
                          <Grid item xs={6}>
                            <Field
                              component={TextField}
                              fullWidth
                              name="aircraft_install"
                              label="@A/C Unit Fitted"
                              type="number"
                              required
                            />
                          </Grid>
                        ) : null}
                      </>
                    ) : null}
                    {dateAxis ? (
                      <Grid item xs={6}>
                        <TenantDatePicker
                          label="Due Off"
                          fullWidth
                          value={values.expiry_date}
                          onChange={(iso) => setFieldValue("expiry_date", iso)}
                          helperText="Blank: worked out from Date On and Period"
                        />
                      </Grid>
                    ) : null}
                    {!condition ? (
                      <>
                        <Grid item xs={6}>
                          <Field
                            component={TextField}
                            fullWidth
                            name="tsn"
                            label="@Install TSN"
                            type="number"
                            disabled={values.tsn_unknown}
                            helperText="Time since new when fitted — the total is worked out"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.tsn_unknown}
                                onChange={(e) => setFieldValue("tsn_unknown", e.target.checked)}
                              />
                            }
                            label="UNK"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Field
                            component={TextField}
                            fullWidth
                            name="tso"
                            label="@Install TSO"
                            type="number"
                            helperText="Time since overhaul when fitted"
                          />
                        </Grid>
                      </>
                    ) : null}
                    {!fitted && !condition ? (
                      <Grid item xs={6}>
                        <Field component={TextField} fullWidth name="time_to_run" label="Time To Run" type="number" />
                      </Grid>
                    ) : null}
                    <Grid item xs={12}>
                      <Field component={TextField} fullWidth multiline minRows={2} name="limitation_notes" label="Notes" />
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        ) : null}
      </SheetContent>
      <SheetActions>
        <CancelButton onClick={onClose} />
        <ActionButton onClick={() => formRef.current?.submitForm()}>{row ? "Save" : "Add"}</ActionButton>
      </SheetActions>
    </Sheet>
  );
};

export default ComponentLimitationSheet;
